import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, FileText, CheckCircle, Clock, XCircle, ArrowLeft,
  ChevronRight, User, Stethoscope, Calendar, AlertCircle, Pencil,
  Link, Unlink,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatCurrency, formatDate } from '@/lib/utils';
import { PresupuestoFormDialog } from './PresupuestoFormDialog';
import { TareasRelacionadasPanel } from '@/components/tareas/TareasRelacionadasPanel';
import { usePermisosTareas } from '@/hooks/useTareas';
import { getErrorMessage } from '@/lib/errors';

/* ── Configuración de estado ── */
const ESTADO_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  borrador:   { label: 'Borrador',   color: 'bg-gray-100 text-gray-600',     icon: FileText },
  enviado:    { label: 'Enviado',    color: 'bg-blue-100 text-blue-700',     icon: FileText },
  aceptado:   { label: 'Aceptado',  color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  en_curso:   { label: 'En curso',  color: 'bg-amber-100 text-amber-700',   icon: Clock },
  completado: { label: 'Completado',color: 'bg-green-100 text-green-700',   icon: CheckCircle },
  cancelado:  { label: 'Cancelado', color: 'bg-red-100 text-red-600',       icon: XCircle },
};

const FLUJO_ESTADOS = ['borrador', 'enviado', 'aceptado', 'en_curso', 'completado'] as const;

const TIPO_ITEM_LABEL: Record<string, string> = {
  servicio: 'Servicio', producto: 'Producto',
};

function EstadoBadge({ estado }: { estado: string }) {
  const cfg = ESTADO_CONFIG[estado] ?? { label: estado, color: 'bg-gray-100 text-gray-600', icon: FileText };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

/* ── Sub-view: detalle de presupuesto ── */
function PresupuestoDetail({ presupuestoId, onBack }: { presupuestoId: string; onBack: () => void }) {
  const [editOpen, setEditOpen] = useState(false);
  const { puedeUsar: puedeUsarTareas } = usePermisosTareas();
  const qc = useQueryClient();

  const { data: det, isLoading } = useQuery<any>({
    queryKey: ['presupuesto-detail', presupuestoId],
    queryFn: () => api.get(`/api/presupuestos/${presupuestoId}`).then(r => r.data),
  });

  const estadoMut = useMutation({
    mutationFn: (estado: string) =>
      api.patch(`/api/presupuestos/${presupuestoId}/estado`, { estado }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presupuesto-detail', presupuestoId] });
      qc.invalidateQueries({ queryKey: ['presupuestos'] });
      toast.success('Estado actualizado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al cambiar estado')),
  });

  const desvincularMut = useMutation({
    mutationFn: (citaId: string) =>
      api.delete(`/api/presupuestos/${presupuestoId}/citas/${citaId}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presupuesto-detail', presupuestoId] });
      toast.success('Cita desvinculada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al desvincular cita')),
  });

  if (isLoading) return <div className="p-8 text-center text-text-secondary text-sm">Cargando...</div>;
  if (!det) return null;

  const estadoIdx = FLUJO_ESTADOS.indexOf(det.estado as any);
  const nextEstado = estadoIdx >= 0 && estadoIdx < FLUJO_ESTADOS.length - 1
    ? FLUJO_ESTADOS[estadoIdx + 1] : null;
  const prevEstado = estadoIdx > 0 ? FLUJO_ESTADOS[estadoIdx - 1] : null;
  const isCancelado = det.estado === 'cancelado';
  const isCompletado = det.estado === 'completado';

  const paciente = det.paciente;
  const medico = det.medico;

  return (
    <div className="space-y-4">
      {/* Header con back */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-text-primary truncate">{det.titulo}</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <EstadoBadge estado={det.estado} />
            {det.vigenciaFin && (
              <span className="text-xs text-text-secondary">
                Vigente hasta {formatDate(det.vigenciaFin)}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setEditOpen(true)}
          className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors"
        >
          <Pencil size={15} />
        </button>
      </div>

      {/* Info paciente + médico */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
          <User size={15} className="text-text-secondary flex-shrink-0" />
          <div>
            <p className="text-xs text-text-secondary">Paciente</p>
            <p className="text-sm font-medium text-text-primary">
              {paciente ? `${paciente.nombre} ${paciente.apellidoPaterno ?? ''}` : '—'}
            </p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
          <Stethoscope size={15} className="text-text-secondary flex-shrink-0" />
          <div>
            <p className="text-xs text-text-secondary">Médico</p>
            <p className="text-sm font-medium text-text-primary">
              {medico ? `${medico.nombre} ${medico.apellidoPaterno ?? ''}` : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Saldo */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-xs text-text-secondary mb-2 font-medium uppercase tracking-wide">Resumen financiero</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-text-secondary mb-0.5">Total</p>
            <p className="text-lg font-bold text-text-primary">{formatCurrency(det.total)}</p>
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-0.5">Pagado</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(det.totalPagado ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-text-secondary mb-0.5">Pendiente</p>
            <p className={`text-lg font-bold ${det.saldoPendiente > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {formatCurrency(det.saldoPendiente ?? 0)}
            </p>
          </div>
        </div>
        {(det.descuentoPorcentaje || det.descuentoMonto) && (
          <p className="text-xs text-green-600 text-center mt-2">
            Descuento aplicado:{' '}
            {det.descuentoPorcentaje ? `${det.descuentoPorcentaje}%` : formatCurrency(det.descuentoMonto)}
          </p>
        )}
      </div>

      {/* Flujo de estado */}
      {!isCancelado && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Progreso</p>
          <div className="flex items-center gap-1">
            {FLUJO_ESTADOS.map((e, i) => (
              <div key={e} className="flex items-center gap-1 flex-1">
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= estadoIdx ? 'bg-primary-400' : 'bg-gray-200'}`} />
                {i === FLUJO_ESTADOS.length - 1 && (
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 border-2 ${
                    estadoIdx === i ? 'bg-primary-500 border-primary-500' : 'bg-white border-gray-300'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isCompletado && nextEstado && (
              <button
                onClick={() => estadoMut.mutate(nextEstado)}
                disabled={estadoMut.isPending}
                className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                Avanzar a {ESTADO_CONFIG[nextEstado]?.label}
              </button>
            )}
            {prevEstado && (
              <button
                onClick={() => estadoMut.mutate(prevEstado)}
                disabled={estadoMut.isPending}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-text-secondary hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Regresar a {ESTADO_CONFIG[prevEstado]?.label}
              </button>
            )}
            {!isCompletado && (
              <button
                onClick={() => estadoMut.mutate('cancelado')}
                disabled={estadoMut.isPending}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-xs text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {isCancelado && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
          <p className="text-sm text-red-600 font-medium">Presupuesto cancelado</p>
          <button
            onClick={() => estadoMut.mutate('borrador')}
            className="text-xs text-red-500 underline mt-1"
          >
            Reabrir como borrador
          </button>
        </div>
      )}

      {/* Items */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-text-primary">Conceptos</p>
        </div>
        {det.items?.length ? (
          <div className="divide-y divide-gray-50">
            {det.items.map((item: any, i: number) => (
              <div key={item.id ?? i} className="px-4 py-3 flex items-start gap-3">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 mt-0.5 ${
                  item.tipo === 'servicio' ? 'bg-blue-50 text-blue-600' :
                  'bg-green-50 text-green-600'}`}>
                  {TIPO_ITEM_LABEL[item.tipo] ?? item.tipo}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary font-medium truncate">{item.nombre}</p>
                  {item.descripcion && <p className="text-xs text-text-secondary">{item.descripcion}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-text-primary">{formatCurrency(item.subtotal)}</p>
                  <p className="text-xs text-text-secondary">
                    {item.cantidad} × {formatCurrency(item.precioUnitario)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="px-4 py-4 text-sm text-text-secondary text-center">Sin conceptos</p>
        )}
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
          <span className="text-sm text-text-secondary">Subtotal</span>
          <span className="text-sm font-medium text-text-primary">{formatCurrency(det.subtotal)}</span>
        </div>
      </div>

      {/* Citas vinculadas */}
      {det.citas?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-text-primary">Citas vinculadas</p>
            <span className="text-xs text-text-secondary">{det.citas.length} cita{det.citas.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-gray-50">
            {det.citas.map((cita: any) => (
              <div key={cita.id} className="px-4 py-3 flex items-center gap-3">
                <Calendar size={14} className="text-text-secondary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">
                    {formatDate(cita.fechaInicio)}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {cita.medico ? `Dr. ${cita.medico.nombre}` : ''}
                    {cita.estado ? ` · ${cita.estado}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => desvincularMut.mutate(cita.id)}
                  disabled={desvincularMut.isPending}
                  title="Desvincular cita"
                  className="p-1.5 text-gray-300 hover:text-red-400 disabled:opacity-50 transition-colors"
                >
                  <Unlink size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {det.descripcion && (
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-text-secondary font-medium mb-1">Descripción</p>
          <p className="text-sm text-text-primary">{det.descripcion}</p>
        </div>
      )}
      {det.notas && (
        <div className="bg-amber-50 rounded-xl p-3">
          <p className="text-xs text-amber-600 font-medium mb-1">Notas</p>
          <p className="text-sm text-text-primary">{det.notas}</p>
        </div>
      )}

      {puedeUsarTareas && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <TareasRelacionadasPanel entidadTipo="presupuesto" entidadId={presupuestoId} etiqueta={det.titulo} />
        </div>
      )}

      <PresupuestoFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        presupuesto={det}
        onCreated={() => qc.invalidateQueries({ queryKey: ['presupuesto-detail', presupuestoId] })}
      />
    </div>
  );
}

/* ── Vista principal ── */
export function PresupuestosTab() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');

  const { data: presupuestos = [], isLoading } = useQuery<any[]>({
    queryKey: ['presupuestos', filtroEstado],
    queryFn: () => api.get('/api/presupuestos', { params: filtroEstado ? { estado: filtroEstado } : {} })
      .then(r => r.data),
  });

  if (selectedId) {
    return (
      <PresupuestoDetail
        presupuestoId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Presupuestos</h2>
          <p className="text-sm text-text-secondary">{presupuestos.length} presupuesto{presupuestos.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <Plus size={15} /> Nuevo presupuesto
        </button>
      </div>

      {/* Filtro de estado */}
      <div className="flex gap-2 flex-wrap">
        {[{ value: '', label: 'Todos' }, ...Object.entries(ESTADO_CONFIG).map(([v, c]) => ({ value: v, label: c.label }))].map(f => (
          <button
            key={f.value}
            onClick={() => setFiltroEstado(f.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filtroEstado === f.value
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="py-8 text-center text-text-secondary text-sm">Cargando...</div>
      ) : presupuestos.length === 0 ? (
        <div className="py-12 text-center">
          <FileText size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-text-secondary">
            {filtroEstado ? `Sin presupuestos en estado "${ESTADO_CONFIG[filtroEstado]?.label}"` : 'Sin presupuestos registrados'}
          </p>
          <button
            onClick={() => setFormOpen(true)}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Crear el primero
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {presupuestos.map((p: any) => {
            const pacienteNombre = p.paciente
              ? `${p.paciente.nombre} ${p.paciente.apellidoPaterno ?? ''}`
              : '—';
            const medicoNombre = p.medico
              ? `Dr. ${p.medico.nombre} ${p.medico.apellidoPaterno ?? ''}`
              : '—';
            const saldoPend = p.saldoPendiente ?? 0;
            const completoPago = saldoPend === 0 && p.total > 0;

            return (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-medium text-text-primary text-sm truncate">{p.titulo}</p>
                      <EstadoBadge estado={p.estado} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-text-secondary flex-wrap">
                      <span className="flex items-center gap-1">
                        <User size={11} /> {pacienteNombre}
                      </span>
                      <span className="flex items-center gap-1">
                        <Stethoscope size={11} /> {medicoNombre}
                      </span>
                      {p.numeroCitas > 0 && (
                        <span className="flex items-center gap-1">
                          <Calendar size={11} /> {p.numeroCitas} cita{p.numeroCitas !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-text-primary">{formatCurrency(p.total)}</p>
                    {saldoPend > 0 && (
                      <p className="text-xs text-amber-600 flex items-center gap-0.5 justify-end mt-0.5">
                        <AlertCircle size={10} /> {formatCurrency(saldoPend)} pend.
                      </p>
                    )}
                    {completoPago && (
                      <p className="text-xs text-green-600 flex items-center gap-0.5 justify-end mt-0.5">
                        <CheckCircle size={10} /> Pagado
                      </p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-400 self-center transition-colors flex-shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* FAB mobile */}
      <button
        onClick={() => setFormOpen(true)}
        className="sm:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center"
        aria-label="Nuevo presupuesto"
      >
        <Plus size={24} />
      </button>

      <PresupuestoFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}
